import Head from 'next/head';
import { Fragment, type ReactNode } from 'react';

import clsxm from '@/lib/clsxm';

import type { Mark, ResolvedCell } from './types';
import { tone } from '../primitives';

/** When the competitor facts on these pages were last checked. */
export const LAST_CHECKED = 'October 2026';
export const LAST_CHECKED_ISO = '2026-10-21';

export const SITE = 'https://easy-peasy.ai';

/** Yes / no / partly, as a small round glyph with a label for screen readers. */
export function MarkGlyph({
  mark,
  className,
}: {
  mark: Mark;
  className?: string;
}) {
  const label = { yes: 'Yes', no: 'No', partial: 'Partly' }[mark];
  return (
    <span
      role='img'
      aria-label={label}
      className={clsxm(
        'inline-flex h-5 w-5 flex-none items-center justify-center rounded-full',
        mark === 'yes' &&
          'bg-emerald-500/[0.12] text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300',
        mark === 'no' &&
          'bg-[#0f0f28]/[0.06] text-[#6f6f86] dark:bg-white/[0.08] dark:text-[#9a9ab4]',
        mark === 'partial' &&
          'bg-amber-500/[0.14] text-amber-700 dark:bg-amber-400/15 dark:text-amber-300',
        className,
      )}
    >
      <svg viewBox='0 0 20 20' aria-hidden='true' className='h-3 w-3'>
        {mark === 'yes' && (
          <path
            d='M5 10.5l3.2 3L15 6.5'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.4'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        )}
        {mark === 'no' && (
          <path
            d='M6.5 6.5l7 7m0-7l-7 7'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.2'
            strokeLinecap='round'
          />
        )}
        {mark === 'partial' && (
          <path
            d='M5.5 10h9'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.4'
            strokeLinecap='round'
          />
        )}
      </svg>
    </span>
  );
}

/** Footnote links to the numbered sources at the bottom of the page. */
export function SourceRefs({
  ids,
  invert = false,
}: {
  ids?: number[];
  /** On a dark stage. */
  invert?: boolean;
}) {
  if (!ids?.length) return null;
  return (
    <span className='ms-1 inline-flex gap-0.5 align-super'>
      {ids.map((id) => (
        <a
          key={id}
          href={`#source-${id}`}
          aria-label={`Source ${id}`}
          className={clsxm(
            'rounded px-0.5 text-[10px] font-semibold leading-none',
            invert
              ? 'text-primary-300 hover:bg-white/10 hover:text-white'
              : 'text-primary-600 hover:bg-primary-50 hover:text-primary-800 dark:text-primary-300 dark:hover:bg-primary-400/10',
          )}
        >
          [{id}]
        </a>
      ))}
    </span>
  );
}

/** A comparison cell's mark, text and sources, laid out inline. */
export function CellValue({
  cell,
  size = 'md',
  linkDocs = false,
}: {
  cell: ResolvedCell;
  size?: 'sm' | 'md';
  /** Link our side's text to its docs page. */
  linkDocs?: boolean;
}) {
  return (
    <span className='flex items-start gap-2.5'>
      {cell.mark && <MarkGlyph mark={cell.mark} className='mt-0.5' />}
      <span
        className={clsxm(
          'min-w-0',
          size === 'sm' ? 'text-sm leading-6' : 'text-[15px] leading-6',
          tone.body,
        )}
      >
        {linkDocs && cell.href ? (
          <a
            href={cell.href}
            className='underline decoration-[#0f0f28]/20 decoration-dotted underline-offset-[4px] transition-colors hover:text-primary-700 hover:decoration-solid hover:decoration-primary-400 dark:decoration-white/25 dark:hover:text-primary-300'
          >
            {cell.text}
          </a>
        ) : (
          cell.text
        )}
        <SourceRefs ids={cell.sources} />
      </span>
    </span>
  );
}

/** "Checked October 2026" with a calendar glyph. */
export function CheckedNote({ className }: { className?: string }) {
  return (
    <span
      className={clsxm(
        'inline-flex items-center gap-1.5 text-sm',
        tone.quiet,
        className,
      )}
    >
      <svg
        viewBox='0 0 20 20'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.6'
        aria-hidden='true'
        className='h-4 w-4 flex-none'
      >
        <rect x='3' y='4.5' width='14' height='12.5' rx='2.5' />
        <path strokeLinecap='round' d='M3 8.5h14M7 3v3M13 3v3' />
      </svg>
      Last checked: {LAST_CHECKED}
    </span>
  );
}

/** JSON-LD for the page, escaped for a <script> tag. */
export function JsonLd({ data }: { data: unknown }) {
  return (
    <Head>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(data).replace(/</g, '\\u003c'),
        }}
      />
    </Head>
  );
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE}${item.path}`,
    })),
  };
}

/** "$16", "$16.50". */
export const usd = (value: number) =>
  `$${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)}`;

/**
 * Keeps "Easy-Peasy.AI" on one line in headings: browsers may break a line
 * at its hyphen, which reads as two words.
 */
export function brandSafe(text: string): ReactNode {
  const parts = text.split('Easy-Peasy.AI');
  if (parts.length === 1) return text;
  return parts.map((part, index) => (
    <Fragment key={index}>
      {index > 0 && <span className='whitespace-nowrap'>Easy-Peasy.AI</span>}
      {part}
    </Fragment>
  ));
}

/** Our mascot on a white tile, beside the "Easy-Peasy.AI" name. */
export function OurMark({ className }: { className?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src='/apple-touch-icon.png'
      alt=''
      width={32}
      height={32}
      className={clsxm(
        'h-8 w-8 flex-none rounded-lg bg-white object-contain p-0.5 ring-1 ring-[#0f0f28]/[0.08] dark:ring-white/10',
        className,
      )}
    />
  );
}
